// @ts-nocheck — W5.1: OL (openlayers) não tem tipos TS no projeto; ErrorBoundary fallback para SmartMap
/**
 * SmartMapFallback — W5.1
 *
 * SmartMap.tsx (W2, @ts-nocheck) uses React 16 string refs (`this.refs.map`) which
 * crash in React 19 with "Expected ref to be a function or createRef()".
 *
 * This component provides:
 *   1. An ErrorBoundary wrapping SmartMap — catches the string-ref crash
 *   2. A lightweight OL 5.x map fallback when SmartMap fails
 *   3. Pass-through when SmartMap renders successfully
 *
 * SmartMap refactor (string refs → createRef) is deferred to a dedicated wave.
 * This file is the W5.1 deliverable that keeps the pages functional in React 19.
 */

import React, { Component, createRef } from "react";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import { fromLonLat } from "ol/proj";
import { Style, Stroke, Fill, Circle as CircleStyle } from "ol/style";
import "ol/ol.css";

// ─── Error Boundary ────────────────────────────────────────────────────────────

interface EBState {
  hasError: boolean;
  error: Error | null;
}

class SmartMapErrorBoundary extends Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  EBState
> {
  state: EBState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): EBState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.warn(
      "[SmartMapFallback] SmartMap crashed in React 19 (string refs / lifecycle).",
      "Rendering OL fallback map. Refactor deferred to dedicated wave.",
      error.message
    );
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// ─── OL Fallback Map ───────────────────────────────────────────────────────────

interface OLFallbackMapProps {
  /** GeoJSON geometry or array of geometries to display */
  geoJson?: unknown;
  /** Map initial center [lon, lat] — defaults to centre of Brazil */
  defaultCenter?: [number, number];
  defaultZoom?: number;
  style?: React.CSSProperties;
}

class OLFallbackMap extends Component<OLFallbackMapProps> {
  private mapRef = createRef<HTMLDivElement>();
  private map: Map | null = null;
  private vectorSource = new VectorSource();

  static defaultProps = {
    defaultCenter: [-55.0, -15.0] as [number, number],
    defaultZoom: 5,
  };

  componentDidMount() {
    if (!this.mapRef.current) return;

    const { defaultCenter, defaultZoom } = this.props as Required<OLFallbackMapProps>;

    const vectorLayer = new VectorLayer({
      source: this.vectorSource,
      style: new Style({
        stroke: new Stroke({ color: "#1677ff", width: 2 }),
        fill: new Fill({ color: "rgba(22, 119, 255, 0.1)" }),
        image: new CircleStyle({
          radius: 7,
          fill: new Fill({ color: "#1677ff" }),
          stroke: new Stroke({ color: "#fff", width: 2 }),
        }),
      }),
    });

    this.map = new Map({
      target: this.mapRef.current,
      layers: [
        new TileLayer({ source: new OSM() }),
        vectorLayer,
      ],
      view: new View({
        center: fromLonLat(defaultCenter),
        zoom: defaultZoom,
      }),
    });

    this.updateGeoJson(this.props.geoJson);
  }

  componentDidUpdate(prevProps: OLFallbackMapProps) {
    if (prevProps.geoJson !== this.props.geoJson) {
      this.updateGeoJson(this.props.geoJson);
    }
  }

  componentWillUnmount() {
    this.map?.setTarget(undefined as any);
    this.map = null;
  }

  updateGeoJson(geoJson: unknown) {
    this.vectorSource.clear();
    if (!geoJson) return;

    try {
      const format = new GeoJSON();
      const items = Array.isArray(geoJson) ? geoJson : [geoJson];
      const features = items
        .filter(Boolean)
        .flatMap((item) => {
          try {
            return format.readFeatures(
              typeof item === "string" ? JSON.parse(item) : item,
              { featureProjection: "EPSG:3857", dataProjection: "EPSG:4326" }
            );
          } catch {
            return [];
          }
        });

      if (features.length > 0) {
        this.vectorSource.addFeatures(features);
        const extent = this.vectorSource.getExtent();
        if (extent && isFinite(extent[0])) {
          this.map?.getView().fit(extent, { padding: [40, 40, 40, 40], maxZoom: 18 });
        }
      }
    } catch (err) {
      console.warn("[OLFallbackMap] Failed to parse geoJson:", err);
    }
  }

  render() {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <div
          ref={this.mapRef}
          style={{ width: "100%", height: "100%", minHeight: 400, ...this.props.style }}
        />
        <div
          style={{
            position: "absolute",
            top: 4,
            left: 4,
            background: "rgba(255,193,7,0.9)",
            color: "#333",
            fontSize: 11,
            padding: "2px 6px",
            borderRadius: 4,
            pointerEvents: "none",
          }}
        >
          Mapa básico (SmartMap indisponível em React 19 — refactor pendente)
        </div>
      </div>
    );
  }
}

// ─── Public API ────────────────────────────────────────────────────────────────

interface SmartMapFallbackProps {
  /** All SmartMap props are passed through */
  mapKey?: string;
  geoJson?: unknown;
  identifyOnExtent?: (extent: number[]) => void;
  identifyOnPixelTolerance?: number;
  defaultCenter?: [number, number];
  defaultZoom?: number;
  overlays?: string[];
  controls?: unknown[];
  infoText?: React.ReactNode;
  [key: string]: unknown;
}

// Static import: SmartMap module loads fine; it only crashes at render time (string refs)
// The ErrorBoundary catches the render-time crash and shows OLFallbackMap instead.
import SmartMapComponent from "./SmartMap";

/**
 * Drop-in replacement for SmartMap usage sites.
 * Wraps SmartMap in an ErrorBoundary; falls back to a plain OL map when SmartMap crashes.
 */
export default class SmartMapFallback extends Component<SmartMapFallbackProps> {
  render() {
    const fallback = (
      <OLFallbackMap
        geoJson={this.props.geoJson}
        defaultCenter={this.props.defaultCenter}
        defaultZoom={this.props.defaultZoom}
      />
    );

    return (
      <SmartMapErrorBoundary fallback={fallback}>
        <SmartMapComponent {...(this.props as any)} />
      </SmartMapErrorBoundary>
    );
  }
}

/** Re-export mapKey from SmartMap for convenience */
export { mapKey } from "./SmartMap";
